import * as SlackHelper from './SlackHelper'
import { auth } from './Firebase'
import {
  shouldIgnoreError,
  isInIframe,
  getParentSiteUrl,
  COMMON_IGNORED_ERRORS
} from '../../../pure-js/libs/ErrorFilterHelper'

// Additional ignored errors specific to user-web
const USER_WEB_IGNORED_ERRORS: Array<string | RegExp> = [
  ...COMMON_IGNORED_ERRORS,
  'Pending promise was never set',
  /feature named .* was not found/
]

/** Cached custom claims from Firebase ID token, set by PortalAuthProvider for permission-error debugging */
let cachedCustomClaims: Record<string, unknown> | null = null

/**
 * Sets cached custom claims (called by PortalAuthProvider when auth state changes).
 * Used when logging permission errors so ops-error includes claims for investigation.
 */
export const setCachedCustomClaims = (claims: Record<string, unknown> | null): void => {
  cachedCustomClaims = claims
}

/**
 * Gets user context from Firebase auth if available.
 * Includes phone for portal users (phone sign-in).
 */
const getUserContext = () => {
  try {
    const currentUser = auth.currentUser
    if (currentUser) {
      return {
        email: currentUser.email || undefined,
        id: currentUser.uid || undefined,
        phone: (currentUser as { phoneNumber?: string }).phoneNumber || undefined
      }
    }
  } catch {
    // Silently fail if auth is not available
  }
  return undefined
}

/**
 * Builds comprehensive error context for logging
 */
const buildErrorContext = (err: any): string => {
  const errorName = err?.name || 'UnknownError'
  const errorMessage = err?.message || err?.toString() || 'No error message'
  const errorStack = err?.stack || 'No stack trace available'

  // Get additional context
  const url = typeof window !== 'undefined' ? window.location.href : 'N/A'
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'
  const timestamp = new Date().toISOString()

  // Check if embedded in iframe and get parent site URL
  const inIframe = isInIframe()
  const parentSiteUrl = getParentSiteUrl()

  // Get user context (includes phone for portal users)
  const user = getUserContext()
  const phone = user && 'phone' in user ? user.phone : undefined
  const userInfo = user
    ? `email: ${user.email ?? 'undefined'}, id: ${user.id ?? 'undefined'}, phone: ${phone ?? 'undefined'}`
    : 'email: undefined, id: undefined, phone: undefined'

  // Build context string
  const contextLines = [
    `user-web: ${userInfo}`,
    `Error: ${errorName}`,
    `Message: ${errorMessage}`,
    `URL: ${url}`,
    `Timestamp: ${timestamp}`,
    `User-Agent: ${userAgent}`
  ]

  // For permission errors, include custom claims to help investigate Firestore rules
  const isPermissionError =
    typeof errorMessage === 'string' &&
    (errorMessage.includes('Missing or insufficient permissions') || errorMessage.includes('insufficient permissions'))
  if (isPermissionError && cachedCustomClaims) {
    contextLines.push(`Custom claims: ${JSON.stringify(cachedCustomClaims)}`)
  }

  // Add iframe context if embedded
  if (inIframe) {
    contextLines.push(`Embedded in iframe: true`)
    if (parentSiteUrl) {
      contextLines.push(`Parent site URL: ${parentSiteUrl}`)
    }
  }

  contextLines.push(`Stack: ${errorStack}`)

  return contextLines.join('\n')
}

/**
 * Checks if we're running in development mode
 */
const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development'
}

/** Stages of the portal phone login flow for ops-error context */
export enum PortalLoginErrorStage {
  RequestPortalLoginSMS = 'requestPortalLoginSMS',
  SendVerificationCode = 'sendVerificationCode',
  VerifyCode = 'verifyCode',
  RequestPortalLoginEmailLink = 'requestPortalLoginEmailLink',
  SendEmailLink = 'sendEmailLink',
  CompleteEmailLink = 'completeEmailLink'
}

export interface ReportPortalLoginErrorOptions {
  stage: PortalLoginErrorStage
  phoneNumber: string
  /** Backend i18n message id when requestPortalLoginSMS returns success: false */
  backendMessageId?: string
  /** Error from Firebase Auth, network, or JS */
  error?: unknown
}

const BACKEND_PORTAL_LOGIN_HINTS: Record<string, string> = {
  portalLoginInvalidPhone: 'Validate phone format (E.164). Check UserFunctions / portal validation.',
  portalLoginNoUser: 'No enrollment_user for this phone — confirm correct number and Firestore data.',
  portalLoginMultipleUsers: 'Multiple enrollment_users share this phone — resolve duplicate enrollment_user rows.',
  portalLoginSmsError: 'Server-side failure in requestPortalLoginSMS — check function logs and dependencies.',
  portalLoginRateLimited: 'Rate limit triggered — review abuse, IP, and backoff settings.',
  portalLoginEmailPhoneMismatch: 'Phone + email pair did not match enrollment_users — user typo or wrong account.',
  portalLoginEmailLinkInvalidSource:
    'requestPortalLoginEmailLink called without source:portal — audit unexpected caller.'
}

const FIREBASE_PORTAL_LOGIN_HINTS: Record<string, string> = {
  'auth/invalid-app-credential':
    'Firebase Phone Auth / App Check / reCAPTCHA: verify Firebase Console → Auth → Phone, SHA keys, domain allowlist, Enterprise key.',
  'auth/captcha-check-failed':
    'First check the *URL* above: if this is a client portal subdomain/custom domain, add that exact hostname under Firebase Console → Authentication → Settings → Authorized domains (no wildcards). See docs/CLIENT_PORTAL_DOMAIN_CHECKLIST.md. Otherwise: bot score or Enterprise key mismatch.',
  'auth/invalid-phone-number': 'Phone not valid E.164 or unsupported region.',
  'auth/quota-exceeded': 'Firebase SMS quota exceeded — billing / quotas in Google Cloud.',
  'auth/app-not-authorized': 'This app is not allowed to use Phone authentication.',
  'auth/too-many-requests': 'Too many SMS requests — Firebase is throttling (wait or review abuse settings).',
  'auth/missing-phone-number': 'Phone number missing when calling signInWithPhoneNumber.',
  'auth/invalid-verification-code': 'User entered wrong code or typo.',
  'auth/code-expired': 'SMS code expired — user should request a new code.',
  'auth/session-expired': 'Phone auth session expired — resend SMS.',
  'auth/network-request-failed': 'Network failure reaching Firebase — user connectivity or outage.',
  'auth/invalid-email': 'Email format invalid when completing email-link sign-in.',
  'auth/invalid-action-code': 'Email link expired or already used — request a new sign-in link.',
  'auth/expired-action-code': 'Email link expired — request a new sign-in link.'
}

/**
 * Sends a structured message to #ops-errors when portal login fails (SMS request, send, or verify).
 * Always reported in production (does not use shouldIgnoreError). Skips Slack in development (console only).
 */
export const reportPortalLoginErrorToOps = (options: ReportPortalLoginErrorOptions): void => {
  const { stage, phoneNumber, backendMessageId, error } = options

  if (isDevelopment()) {
    console.error('[Local Portal Login Error — not sent to Slack]', options)
    return
  }

  const lines: string[] = [
    '🔐 *user-web — portal phone login failure*',
    `*Stage:* ${stage}`,
    `*Phone:* ${phoneNumber}`,
    `*Timestamp:* ${new Date().toISOString()}`,
    `*URL:* ${typeof window !== 'undefined' ? window.location.href : 'N/A'}`,
    `*User-Agent:* ${typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'}`
  ]

  if (isInIframe()) {
    lines.push(`*Embedded in iframe:* true`)
    const parentSiteUrl = getParentSiteUrl()
    if (parentSiteUrl) {
      lines.push(`*Parent site URL:* ${parentSiteUrl}`)
    }
  }

  if (backendMessageId) {
    lines.push(`*Backend messageId:* ${backendMessageId}`)
    const hint = BACKEND_PORTAL_LOGIN_HINTS[backendMessageId]
    if (hint) {
      lines.push(`*Troubleshoot (backend):* ${hint}`)
    }
  }

  if (error !== undefined && error !== null) {
    const err = error as { code?: string; name?: string; message?: string; stack?: string }
    const message = err?.message ?? (typeof error === 'string' ? error : String(error))
    if (err?.code) {
      lines.push(`*Firebase / error code:* ${err.code}`)
    }
    if (err?.name) {
      lines.push(`*Error name:* ${err.name}`)
    }
    lines.push(`*Message:* ${message}`)
    if (err?.code && FIREBASE_PORTAL_LOGIN_HINTS[err.code]) {
      lines.push(`*Troubleshoot (Firebase):* ${FIREBASE_PORTAL_LOGIN_HINTS[err.code]}`)
    }
    if (err?.stack) {
      lines.push(`*Stack:* ${err.stack}`)
    }
  }

  SlackHelper.error(null, undefined, lines.join('\n'))
}

export const captureException = (err: any, user?: any) => {
  // Use provided user or try to get from auth
  const userContext = user || getUserContext()

  // Check if error should be ignored (external errors, browser extensions, etc.)
  if (shouldIgnoreError(err, USER_WEB_IGNORED_ERRORS)) {
    if (isDevelopment()) {
      console.debug('[Filtered] Ignoring external/noise error:', err?.name, err?.message)
    }
    return
  }

  // Don't send errors from development mode to Slack - only log to console
  if (isDevelopment()) {
    const errorContext = buildErrorContext(err)
    console.error('[Local Error - Not sent to Slack]', errorContext)
    return
  }

  // Build comprehensive error context
  const errorContext = buildErrorContext(err)

  // Send to Slack with enhanced context
  SlackHelper.error(err, userContext, errorContext)
}
